<script setup name="TvbVideoDetailInfo">
import TvbVideoDetailDescription from './../Description/index.vue'
import TvbPreferredSources from '@/components/TvbPreferredSources/index.vue'
import { inject, computed, ref, onMounted, onUnmounted } from 'vue'
import { postVideoLike, getVideoShare } from '@/api/video'
import { TvbSsoButton } from '@tvb-sz/tvb-headerfooter-frontend'
import { useMessage } from 'naive-ui'
import { copyString } from '@/utils'
import dayjs from 'dayjs'

const getExtra = inject('getExtra', () => {})
// const showList = inject('showList', () => { })
const currentVideo = inject('currentVideo')
const playerWidth = inject('playerWidth', '100%')
const size = inject('size')
// 移动端（s/xs）：追踪按钮置于發佈日期下方，与文章详情页规则一致
const isMobile = computed(() => ['s', 'xs'].includes(size.value))
// 注入顶层 extraLoaded：等 Player 的 getExtra 完成（category_info.path 就绪）再渲染 banner 广告
const extraLoaded = inject('extraLoaded', ref(true))
// LREC 广告真实高度，广告加载成功后动态设置为 Info 区域最低高度
const lrecAdHeight = inject('lrecAdHeight', ref(0))
const title = computed(() => currentVideo.value.title || '')
const isLike = computed(() => currentVideo.value.is_like || false)
const likeNum = computed(() => currentVideo.value.like_num || 0)
const author = computed(() => currentVideo.value.author?.join('、'))
const loginRef = ref()
const message = useMessage()
const popoverShow = ref(false)
const overlap = ref(false)

// 广告相关计算属性
const videoId = computed(() => currentVideo.value?.id)
const categoryInfo = computed(() => currentVideo.value?.category_info)
const isShowAd = computed(() => currentVideo.value?.is_show_ad ?? 1)
// Banner 广告类型：>= 830px 使用 Super Banner (sb)，< 830px 使用 Banner (bn)
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1440)
const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth
}
const bannerAdType = computed(() => (windowWidth.value >= 830 ? 'sb' : 'bn'))

onMounted(() => {
  window.addEventListener('resize', updateWindowWidth)
})
onUnmounted(() => {
  window.removeEventListener('resize', updateWindowWidth)
})

const fetchShare = async (type) => {
  const res = await getVideoShare({
    share_id: currentVideo.value.id,
    type: type
  })
  if (res.code !== 0) throw new Error(res.message)
  const shareInfo = res?.data
  if (!shareInfo) throw new Error('未获取到数据')
  let str = `${shareInfo.title}\r\n${shareInfo.link ? shareInfo.link : ''}`
  if (shareInfo.content) str = str + `\r\n====================\r\n${shareInfo.content}`
  return { str, data: shareInfo }
}

const shares = ref([
  {
    name: '透過 Facebook 分享',
    icon: 'facebook',
    onClick: async () => {
      try {
        const share = await fetchShare(1)
        window.FB.ui({
          method: 'share',
          href: share?.data?.link ? share.data.link : ''
        })
      } catch (error) {
        message.error(error.message)
      }
    }
  },
  {
    name: '透過 WhatsApp 分享',
    icon: 'whatsapp',
    onClick: async () => {
      try {
        const share = await fetchShare(2)
        window.open(
          `https://api.whatsapp.com/send?text=${encodeURIComponent(share?.str || '')}`,
          '_blank'
        )
      } catch (error) {
        message.error(error.message)
      }
    }
  },
  {
    name: '複製連結',
    icon: 'copy',
    onClick: async () => {
      try {
        const share = await fetchShare(3)
        await copyString(share?.str)
        message.success('已複製連結')
      } catch (error) {
        message.error(error.message)
      }
    }
  }
])

const handleShow = (show) => {
  popoverShow.value = show
}

const handleVideoLike = async () => {
  const res = await postVideoLike({
    type: 2,
    liked_id: currentVideo.value.id,
    status: isLike.value ? 2 : 1,
    video_id: currentVideo.value.id
  })
  if (res.code === 150001) {
    message.error('請先登錄')
    loginRef.value?.login && loginRef.value.login()
  } else if (res.code !== 0) {
    message.error(res.message)
  } else {
    getExtra?.()
    message.success('操作成功')
  }
}

const publishDate = computed(() => {
  if (!currentVideo.value.publish_at) return ''
  let text = `發佈日期：${dayjs.unix(currentVideo.value.publish_at).format('YYYY-MM-DD HH:mm')} `
  if (
    currentVideo.value.updated_at &&
    currentVideo.value.updated_at > currentVideo.value.publish_at
  ) {
    if (['s', 'xs'].includes(size.value)) text += `<br />`
    text += `更新日期：${dayjs.unix(currentVideo.value.updated_at).format('YYYY-MM-DD HH:mm')}`
  }
  return text
})
</script>

<template>
  <div
    class="tvb-video-detail_info"
    :style="{ minHeight: lrecAdHeight ? `${lrecAdHeight}px` : undefined }"
  >
    <div class="tvb-video-detail_info-content" :style="{ width: playerWidth }" :class="[size]">
      <h1 class="tvb-video-detail_info-title">{{ title }}</h1>
      <!-- <div class="tvb-video-detail_info-tabs">
                <button class="tvb-video-detail_info-tab active">簡介</button>
                <button class="tvb-video-detail_info-tab" @click="showList">播放列表</button>
            </div> -->

      <div class="tvb-video-detail_info-other">
        <p class="tvb-video-detail_info-other-write_article">撰文：{{ author }}</p>
        <p
          class="tvb-video-detail_info-other-publish_date"
          v-if="publishDate"
          v-html="publishDate"
        ></p>
      </div>
      <div class="tvb-video-detail_info-other-btns">
        <button class="tvb-video-detail_info-btn" @click="handleVideoLike">
          <tvb-svg-icon
            class="tvb-video-detail_info-btn-icon"
            :name="`like_${isLike ? 'active' : 'default'}`"
          /><span v-if="likeNum">{{ likeNum }}</span>
        </button>
        <n-popover
          :overlap="overlap"
          placement="bottom-end"
          :key="size"
          :trigger="['m', 's', 'xs'].includes(size) ? 'click' : 'hover'"
          :show-arrow="false"
          wrapper-style="tvb-video-detail_info-popover"
          @update:show="handleShow"
        >
          <template #trigger>
            <button class="tvb-video-detail_info-btn" :class="{ 'is-active': popoverShow }">
              <tvb-svg-icon class="tvb-video-detail_info-btn-icon" name="share" />
            </button>
          </template>
          <div class="tvb-video-detail_info-popover-content">
            <div class="tvb-video-detail_info-shares">
              <div
                class="tvb-video-detail_info-share"
                v-for="share in shares"
                :key="share.name"
                @click="share.onClick"
              >
                <tvb-svg-icon class="tvb-video-detail_info-share-icon" :name="share.icon" />
                <p class="tvb-video-detail_info-share-icon-text">{{ share.name }}</p>
              </div>
            </div>
          </div>
        </n-popover>
      </div>
    </div>
    <!-- 移动端：追踪按钮置于發佈日期下方，与文章详情页规则一致 -->
    <tvb-preferred-sources v-if="isMobile" class="tvb-video-detail_info-preferred" />
    <!-- Super Banner / Banner 广告：位于发布日期下方、视频简介上方 -->
    <!-- 加 extraLoaded 条件：等 Player 的 getExtra 完成（category_info.path 就绪）后再渲染 -->
    <tvb-ads
      v-if="videoId && isShowAd !== 2 && extraLoaded"
      :style="{ marginTop: '16px', width: playerWidth }"
      :ad-type="bannerAdType"
      :key="bannerAdType"
      :position="14"
      :unit="`/${videoId}`"
      :show-ad="isShowAd !== 2"
      :params="{
        content_type: 'video',
        vtype: 'vod',
        sub_category_path: categoryInfo?.path || 'x',
        video_id: videoId || 'x'
      }"
    />
    <tvb-video-detail-description />
  </div>

  <tvb-sso-button type="login-logout" ref="loginRef" style="display: none" />
</template>

<style lang="scss" scoped>
.tvb-video-detail_info {
  // width: fit-content;
  @media (min-width: 1144px) {
    // 宽度公式修正：播放器与播放列表固定间距为 24px（原 16px 导致缩放浏览器宽度时内容宽度显示不正确）
    width: calc(var(--player-width) + 400px + 24px);
    margin: 0 auto;
  }
  .tvb-video-detail_info-content {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    // 对齐设计稿间距模型：容器整体 pt16 / pb12，子项间 gap 8（替代原 margin-top + 标题 margin-bottom 的间距方式）
    padding-top: 16px;
    padding-bottom: 12px;
    gap: 8px;

    @media (max-width: 1440px) {
      &.xl {
        // <=1440 断点顶部间距沿用原 12px 规格（原 margin-top 改为 padding-top，避免与基础 padding 叠加）
        padding-top: 12px;
      }
      &.l {
        font-size: 22px;
      }
    }

    &.l,
    &.m {
      .tvb-video-detail_info-title {
        font-size: 22px;
        line-height: 32px;
      }

      .tvb-video-detail_info-other {
        .tvb-video-detail_info-other-write_article,
        .tvb-video-detail_info-other-publish_date {
          font-size: 16px;
          line-height: 24px;
        }
      }
    }

    @media (max-width: 1143px) {
      width: 100% !important;
    }

    &.s,
    &.xs {
      flex-wrap: wrap;
      gap: 10px;
      margin-top: 12px;
      // 移动端沿用原间距模型：重置桌面容器的上下 padding，顶部间距仍由 margin-top 控制
      padding-top: 0;
      padding-bottom: 0;

      .tvb-video-detail_info-title {
        width: 100%;
        font-size: 18px;
        line-height: 26px;
      }

      .tvb-video-detail_info-other {
        .tvb-video-detail_info-other-write_article,
        .tvb-video-detail_info-other-publish_date {
          font-size: 14px;
          line-height: 20px;
          letter-spacing: 0.3px;
        }
      }

      .tvb-video-detail_info-other-btns {
        gap: 10px;
        .tvb-video-detail_info-btn {
          margin-left: 0;
          padding: 10px 11px;

          .tvb-video-detail_info-btn-icon {
            font-size: 18px;
          }
          span {
            line-height: 18px;
          }
        }
      }

      .tvb-video-detail_info-tabs {
        display: flex;
      }
    }

    .tvb-video-detail_info-title {
      width: 100%;
      // 标题颜色对齐设计稿 brand 色：#231f20 → #1c191a
      color: #1c191a;
      font-size: 24px;
      font-weight: 500;
      line-height: 32px;
      letter-spacing: 0.5px;
      // 标题与下方信息的间距改由容器 gap: 8px 统一控制（原 margin-bottom: 6px 已移除）
      margin: 0;
      // margin-right: 12px;
    }
    .tvb-video-detail_info-other {
      .tvb-video-detail_info-other-write_article,
      .tvb-video-detail_info-other-publish_date {
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        letter-spacing: 0.5px;
        color: #666;
      }
      .tvb-video-detail_info-other-publish_date {
        margin-top: 2px;
      }
    }
    .tvb-video-detail_info-other-btns {
      display: flex;
      gap: 12px;
      flex-wrap: nowrap;
      margin-left: auto;
      .tvb-video-detail_info-btn {
        background-color: transparent;
        box-sizing: border-box;
        display: flex;
        padding: 10px 14px;
        justify-content: center;
        align-items: center;
        // 按钮内部 icon 与数字的间距：3px → 4px，对齐设计稿组合间距
        gap: 4px;
        border-radius: 999px;
        // 边框由 outline 改为 border 实现（box-sizing 已含边框），颜色对齐设计稿 rgba(163, 178, 188, 0.35)
        border: 1px solid rgba(163, 178, 188, 0.35);
        outline: none;
        box-sizing: border-box;
        height: fit-content;
        cursor: pointer;

        span {
          color: #231f20;
          font-size: 15px;
          font-weight: 500;
          line-height: 22px;
          letter-spacing: 0.3px;
        }

        &.is-active {
          background: #eceff3;
          border: 1px solid #eceff3;
        }

        .tvb-video-detail_info-btn-icon {
          font-size: 24px;
        }
        &:hover {
          cursor: pointer;
          // hover 背景与分享按钮一致（两类按钮共用同一 class，hover 统一生效；基础 border 保留使反馈更明显）
          background: #eceff3;
        }
      }
    }

    .tvb-video-detail_info-tabs {
      display: none;
      gap: 10px;
      margin-right: auto;

      .tvb-video-detail_info-tab {
        display: flex;
        padding: 6px 16px;
        align-items: center;
        border-radius: 12px;
        background: #f5f6f7;
        color: #231f20;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0.3px;
        border: 0;
        outline: 0;

        &.active {
          background: rgba(0, 85, 189, 0.08);
          color: #0055bd;
        }
      }
    }
  }

  .tvb-video-detail_info-preferred {
    display: none;

    @media (max-width: 768px) {
      display: block;
      margin-top: 12px;
    }
  }
}
</style>

<style lang="scss">
div[wrapper-style='tvb-video-detail_info-popover'] {
  --n-border-radius: 24px !important;
  --n-padding: 0px !important;
  --n-space: 8px !important;

  .tvb-video-detail_info-popover-content {
    border-radius: 24px;
    border: 0.5px solid rgba(163, 178, 188, 0.35);
    box-shadow: 0 4px 14px -4px rgba(0, 0, 0, 0.1);
    .tvb-video-detail_info-shares {
      padding: 8px;

      .tvb-video-detail_info-share {
        display: flex;
        padding: 12px 16px;
        align-items: center;
        gap: 10px;
        border-radius: 16px;
        cursor: pointer;
        box-sizing: border-box;

        &:hover {
          background: #f5f6f7;
        }

        .tvb-video-detail_info-share-icon {
          font-size: 28px;
        }

        .tvb-video-detail_info-share-icon-text {
          color: #231f20;
          font-size: 16px;
          font-weight: 400;
          line-height: 24px;
          letter-spacing: 0.5px;
        }
      }
    }
  }
}
</style>
